import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import WeatherHeader from './WeatherHeader';
import MindanaoSevereWeather from './MindanaoSevereWeather';
import IliganBarangaysWeather from './IliganBarangaysWeather';
import RegionalWeather from './RegionalWeather';
import TyphoonCard from './TyphoonCard';
import TyphoonMap from './TyphoonMap';
import AlertFeed from './AlertFeed';
import ActionableSteps from './ActionableSteps';
import DataSources from './DataSources';
import NewsSection from './NewsSection';
import WeatherFooter from './WeatherFooter';
import SEOContent from './SEOContent';
import DynamicSchema from './DynamicSchema';
import { fetchEnhancedWeatherData } from '../services/enhancedWeatherService';
import { trackPerformance, trackDataRefresh } from '../utils/analytics';

const CACHE_DURATION = 30 * 60 * 1000;

const WeatherDashboard = () => {
  const [weatherData, setWeatherData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);
  const [showMap, setShowMap] = useState(true);

  const loadWeatherData = useCallback(async (forceRefresh = false) => {
    const startTime = Date.now();
    try {
      if (!weatherData) setLoading(true);
      setError(null);
      
      const data = await fetchEnhancedWeatherData();
      setWeatherData(data);
      setLastUpdated(new Date());
      trackDataRefresh('PAGASA/Mixed', true);
    } catch (err) {
      console.error('Dashboard data load error:', err);
      setError('Connection timeout. Displaying cached/regional data.');
      trackDataRefresh('PAGASA/Mixed', false, err.message);
    } finally {
      setLoading(false);
      trackPerformance(Date.now() - startTime, 30);
    }
  }, [weatherData]);

  useEffect(() => {
    loadWeatherData();
    const interval = setInterval(() => loadWeatherData(true), CACHE_DURATION);
    return () => clearInterval(interval);
  }, [loadWeatherData]);

  // Non-blocking Render Strategy:
  // We render the shell immediately. Components handle null data gracefully.
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
      <DynamicSchema 
        weatherData={weatherData} 
        typhoonData={weatherData?.typhoon} 
      />

      <div className="container mx-auto px-4 py-6 max-w-7xl flex-grow">
        <motion.main 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          className="space-y-8"
        >
          {/* Header remains interactive even if loading */}
          <WeatherHeader 
            lastUpdated={lastUpdated} 
            onRefresh={() => loadWeatherData(true)} 
            loading={loading} 
            hasTyphoon={!!weatherData?.typhoon} 
            onToggleMap={() => setShowMap(!showMap)} 
            showMap={showMap} 
            weatherData={weatherData} 
            typhoonData={weatherData?.typhoon} 
          />

          {error && (
            <motion.div 
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              className="bg-red-50 border border-red-200 p-3 rounded-xl text-red-800 text-xs font-medium flex items-center justify-center"
            >
              <span className="mr-2">⚠️</span> {error}
            </motion.div>
          )}

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <AnimatePresence mode="wait">
                {weatherData?.typhoon ? (
                  <TyphoonCard key="typhoon" data={weatherData.typhoon} />
                ) : null}
              </AnimatePresence>
              
              {/* This component handles its own skeleton internally */}
              <IliganBarangaysWeather 
                data={weatherData?.regional} 
                typhoonData={weatherData?.typhoon} 
                outlook={weatherData?.outlook} 
                loading={loading && !weatherData}
              />

              {(showMap || weatherData?.typhoon) && (
                <TyphoonMap 
                  typhoonData={weatherData?.typhoon} 
                  isVisible={showMap || !!weatherData?.typhoon} 
                />
              )}
            </div>

            <aside className="lg:col-span-1 space-y-8">
              <AlertFeed 
                alerts={weatherData?.alerts} 
                loading={loading && !weatherData} 
              />
              
              <ActionableSteps 
                hasTyphoon={!!weatherData?.typhoon} 
                signals={weatherData?.typhoon?.signals} 
              />
            </aside>
          </div>

          <section className="space-y-8">
            <MindanaoSevereWeather 
              data={weatherData?.regional} 
              typhoonData={weatherData?.typhoon} 
            />

            <RegionalWeather data={weatherData?.regional} />

            <SEOContent weatherData={weatherData} />

            <div className="grid lg:grid-cols-2 gap-8">
              <DataSources sources={weatherData?.sources} />
              <NewsSection 
                reliefWebData={weatherData?.additionalSources?.reliefWeb} 
                gdacsData={weatherData?.additionalSources?.gdacs} 
              />
            </div>
          </section>
        </motion.main>
      </div>
      <WeatherFooter />
    </div>
  );
};

export default WeatherDashboard;